/**
 * notifications.meta.js
 * Single source of truth for notification domain constants + small pure helpers.
 */

// ─── Channels (delivery medium) ───────────────────────────────────────────────

export const CHANNELS = {
  email: { key: 'email', label: 'Email', icon: 'mail' },
  whatsapp: { key: 'whatsapp', label: 'WhatsApp', icon: 'chat', note: 'Templates + SID managed in Twilio' },
};

export const CHANNEL_ORDER = ['email', 'whatsapp'];

// ─── Events (trigger source) ──────────────────────────────────────────────────

export const EVENTS = {
  'recommendation.created': { key: 'recommendation.created', label: 'New Recommendation', group: 'RECOMMENDATIONS' },
  'recommendation.high_impact': { key: 'recommendation.high_impact', label: 'High-Impact Recommendation', group: 'RECOMMENDATIONS' },
  'recommendation.expired': { key: 'recommendation.expired', label: 'Recommendation Expired Unactioned', group: 'RECOMMENDATIONS' },
  'parameter.out_of_band': { key: 'parameter.out_of_band', label: 'Parameter Out of Optimal Band', group: 'VARIABLE HEALTH' },
  'parameter.sensor_offline': { key: 'parameter.sensor_offline', label: 'Sensor Offline / No Data', group: 'VARIABLE HEALTH' },
  'parameter.off_days_threshold': { key: 'parameter.off_days_threshold', label: 'Off-Days Threshold Crossed', group: 'VARIABLE HEALTH' },
  'deployment.status_change': { key: 'deployment.status_change', label: 'Deployment Status Changed', group: 'SYSTEM' },
};

export const EVENT_GROUPS = ['RECOMMENDATIONS', 'VARIABLE HEALTH', 'SYSTEM'];

// Bundled fallback for the BE event catalog (GET /notification-events). Used when
// the BE list is empty/unavailable so the form still works. Array form mirrors
// the endpoint response shape: [{ key, label, group }].
export const FALLBACK_EVENTS = Object.values(EVENTS);

// ─── Trigger timing ───────────────────────────────────────────────────────────

export const TRIGGER_TYPES = {
  instant: { key: 'instant', label: 'Instant', description: 'Fires in real time, every time the event occurs.', icon: 'bolt' },
  one_time: { key: 'one_time', label: 'One-time', description: 'Fires once at a scheduled date & time, then auto-disables.', icon: 'event' },
  recurring: { key: 'recurring', label: 'Recurring', description: 'Fires on a repeating daily or weekly schedule.', icon: 'autorenew' },
};

export const TRIGGER_ORDER = ['instant', 'one_time', 'recurring'];

export const SCHEDULE_FREQUENCIES = {
  daily: { key: 'daily', label: 'Daily', description: 'Once per day at the chosen time' },
  weekly: { key: 'weekly', label: 'Weekly', description: 'On selected weekdays at the chosen time' },
};

export const WEEKDAYS = [
  { key: 'mon', label: 'Mon', full: 'Monday' },
  { key: 'tue', label: 'Tue', full: 'Tuesday' },
  { key: 'wed', label: 'Wed', full: 'Wednesday' },
  { key: 'thu', label: 'Thu', full: 'Thursday' },
  { key: 'fri', label: 'Fri', full: 'Friday' },
  { key: 'sat', label: 'Sat', full: 'Saturday' },
  { key: 'sun', label: 'Sun', full: 'Sunday' },
];

// ─── Status visuals ───────────────────────────────────────────────────────────

export const STATUS_META = {
  active: { label: 'Active', color: '#16a34a', bg: 'rgba(22,163,74,0.10)', border: 'rgba(22,163,74,0.25)' },
  paused: { label: 'Paused', color: '#64748b', bg: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.25)' },
};

// ─── Validation helpers (pure) ────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// E.164-ish: optional +, 8–15 digits.
const PHONE_RE = /^\+?[0-9]{8,15}$/;

export function isValidEmail(v) {
  return EMAIL_RE.test(String(v).trim());
}

export function isValidPhone(v) {
  return PHONE_RE.test(String(v).trim().replace(/[\s-]/g, ''));
}

/** Human-readable recipient count summary for the table. */
export function recipientSummary(notification) {
  if (!notification) return '—';
  if (notification.channel === 'whatsapp') {
    const n = notification.recipients?.phones?.length ?? 0;
    return n ? `${n} number${n === 1 ? '' : 's'}` : 'No numbers';
  }
  const to = notification.recipients?.to?.length ?? 0;
  const cc = notification.recipients?.cc?.length ?? 0;
  if (!to && !cc) return 'No recipients';
  const parts = [];
  if (to) parts.push(`${to} to`);
  if (cc) parts.push(`${cc} cc`);
  return parts.join(' · ');
}

/** Short human label for a notification's trigger/schedule. */
export function triggerSummary(notification) {
  const t = notification?.trigger;
  if (!t) return '—';
  if (t.type === 'instant') return 'Instant';
  if (t.type === 'one_time') return 'One-time';
  if (t.type === 'recurring') {
    const freq = t.schedule?.frequency;
    if (freq === 'weekly') {
      const days = t.schedule?.days ?? [];
      return `Weekly · ${days.length} day${days.length === 1 ? '' : 's'}`;
    }
    return 'Daily';
  }
  return TRIGGER_TYPES[t.type]?.label ?? '—';
}

/**
 * Validate a notification draft. Returns a map of field -> error message.
 * Empty object means valid.
 */
export function validateNotification(draft) {
  const errors = {};
  if (!draft.name?.trim()) errors.name = 'Title is required.';
  if (!draft.channel) errors.channel = 'Choose a delivery medium.';
  if (!draft.event) errors.event = 'Choose a trigger event.';
  // templateId is resolved from event+channel (one template per pair). If it's
  // missing once an event is chosen, no template exists for that event+channel.
  if (draft.event && !draft.templateId) errors.event = 'No template is available for this event on the selected channel.';

  if (draft.channel === 'whatsapp') {
    if (!draft.waSid?.trim()) errors.waSid = 'Twilio Messaging SID is required for WhatsApp.';
    const phones = draft.recipients?.phones ?? [];
    if (phones.length === 0) errors.recipients = 'Add at least one phone number.';
    else if (phones.some((p) => !isValidPhone(p))) errors.recipients = 'One or more phone numbers are invalid.';
  } else if (draft.channel === 'email') {
    const to = draft.recipients?.to ?? [];
    const cc = draft.recipients?.cc ?? [];
    if (to.length === 0) errors.recipients = 'Add at least one “To” recipient.';
    else if ([...to, ...cc].some((e) => !isValidEmail(e))) errors.recipients = 'One or more email addresses are invalid.';
  }

  const t = draft.trigger ?? {};
  if (t.type === 'one_time' && !t.runAt) errors.trigger = 'Pick a date & time for the one-time run.';
  if (t.type === 'recurring') {
    if (!t.schedule?.time) errors.trigger = 'Pick a time of day.';
    else if (t.schedule.frequency === 'weekly' && (t.schedule.days?.length ?? 0) === 0) {
      errors.trigger = 'Select at least one weekday.';
    }
  }
  return errors;
}

// ─── Template preview ─────────────────────────────────────────────────────────

// Sample values used to render a readable preview of a template body.
const PREVIEW_SAMPLES = {
  deployment: 'Plant North (Blast Furnace)',
  parameter: 'Hot Metal Temp',
  value: '1480',
  unit: '°C',
  optimumLow: '1450',
  optimumHigh: '1520',
  recommendation: 'Reduce coke rate by 3 kg/tHM',
  impact: 'High',
  timestamp: '17 Jun 2026, 09:00 AM',
};

/** Replace {{token}} occurrences with bracketed sample values for live preview. */
export function resolvePreview(text) {
  if (!text) return '';
  return text.replace(/\{\{(\w+)\}\}/g, (full, key) =>
    PREVIEW_SAMPLES[key] != null ? `[${PREVIEW_SAMPLES[key]}]` : full);
}

/** A blank draft for the "Add notification" form. */
export function emptyNotification() {
  return {
    name: '',
    description: '',
    channel: 'email',
    event: '',
    templateId: null,
    waSid: '',
    recipients: { to: [], cc: [], phones: [] },
    trigger: { type: 'instant', runAt: null, schedule: { frequency: 'daily', days: [], time: '' } },
    enabled: true,
  };
}
